/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useState, useEffect} from 'react'
import {
    Button,
    Divider,
    Flex,
    Image,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import ProductRegistrationFields from '../../product-registration/partials/forms/product-registration-fields'
import {useForm} from 'react-hook-form'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../../ssr/constants'
import {generateGUID} from '../../../utils/utils'
import {useLocaleLanguage} from '../../../hooks/use-locale-custom'
import {useCustomPreferenceWithLocale} from '../../../hooks/use-custom-preference'

const getRetrievedPurchaseDate = (product) => {
    const purchaseDate = product?.purchaseData || product?.referenceDate
    if (!purchaseDate) return ''

    const dateParts = String(purchaseDate).match(/^(\d{4})(\d{2})(\d{2})/)
    if (dateParts) {
        return new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
    }

    const parsedDate = new Date(purchaseDate)
    return isNaN(parsedDate.getTime()) ? '' : parsedDate
}

const getRetrievedPurchasePrice = (product) => {
    const purchasePrice =
        product?.purchasePriceContentKut ??
        product?.purchasePriceKut ??
        product?.purchasePrice ??
        product?.pricePaid
    if (purchasePrice === null || purchasePrice === undefined || purchasePrice === '') return ''

    const parsedPrice = Number.parseFloat(String(purchasePrice).replace(',', '.'))
    return Number.isFinite(parsedPrice) ? parsedPrice.toFixed(2) : ''
}

const isRetrievedPurchasePriceLocked = (product) => Number(getRetrievedPurchasePrice(product)) !== 0

const ProductRegisteredEditForm = ({goBack, product, confirmEdit, isGuest, confirmDelete}) => {
    const [isPricePaidLocked, setIsPricePaidLocked] = useState(
        isRetrievedPurchasePriceLocked(product),
    )
    const [receiptBase64, setReceiptBase64] = useState('')
    const [receiptName, setReceiptName] = useState('')
    const [receiptMime, setReceiptMime] = useState('')
    const [receiptFile, setReceiptFile] = useState(null) // Add state for the file object
    const [fileError, setFileError] = useState('') // Add state for file validation error
    const [isReceiptScanning, setIsReceiptScanning] = useState(false)
    const [receiptValidated, setReceiptValidated] = useState(false)
    const [receiptTaggunError, setReceiptTaggunError] = useState('')
    const [receiptScanResult, setReceiptScanResult] = useState(null)
    const [showManualFields, setShowManualFields] = useState(false)
    const [allowSourceEditAfterScan, setAllowSourceEditAfterScan] = useState(false)
    const {currency: activeCurrency} = useCurrency()
    const appOrigin = useOrigin(false)
    const lang = useLocaleLanguage()
    const merchantNameAllowList = useCustomPreferenceWithLocale('showProductRegistrationReason')
    const {
        data: productData,
        isLoading,
        error,
    } = useProduct({
        parameters: {
            id: product?.skuKut,
            allImages: false,
            currency: activeCurrency,
        },
    })
    const largeImage = productData?.imageGroups?.find((item) => item.viewType === 'large')
        ?.images[0]?.link
    const smallImage = productData?.imageGroups?.find((item) => item.viewType === 'medium')
        ?.images[0]?.link

    const selectedProduct = product
    const form = useForm({
        defaultValues: {
            //serialNumber: product?.serialId || '',
            purchasedate: product?.purchaseData || '',
            source: product?.source || '',
            pricePaid: '',
        },
    })
    const {setValue} = form
    const styles = useMultiStyleConfig('ProductRegistration', {
        variant: 'editRegisteredProductForm',
    })
    const formStyles = useMultiStyleConfig('ProductRegistrationForm', {
        variant: 'editRegisteredProductForm',
    })

    useEffect(() => {
        if (selectedProduct) {
            const purchaseDate = getRetrievedPurchaseDate(selectedProduct)
            //setValue('serialNumber', selectedProduct.serialId)
            setValue('productName', selectedProduct.productName)
            // purchasedate needs to be kept as a Date, not string
            setValue('purchasedate', purchaseDate)
            setValue('source', selectedProduct.sourceKut)
            setValue('serialNumber', selectedProduct.serialNumberKut)
            setValue('pricePaid', getRetrievedPurchasePrice(selectedProduct))
            setIsPricePaidLocked(isRetrievedPurchasePriceLocked(selectedProduct))
        }
    }, [selectedProduct, setValue])

    const restoreRetrievedPurchaseData = () => {
        form.setValue('purchasedate', getRetrievedPurchaseDate(product))
        form.setValue('pricePaid', getRetrievedPurchasePrice(product))
        setIsPricePaidLocked(isRetrievedPurchasePriceLocked(product))
        form.setValue('source', '')
        form.setValue('serialNumber', '')
    }

    const clearFieldsForReceiptData = () => {
        form.setValue('purchasedate', '')
        form.setValue('pricePaid', '')
        setIsPricePaidLocked(false)
        form.setValue('source', '')
        form.setValue('serialNumber', '')
    }

    const handleReceiptScan = async (file) => {
        setIsReceiptScanning(true)
        setReceiptScanResult(null)
        setReceiptValidated(false)
        setReceiptTaggunError('')
        setShowManualFields(false)
        setAllowSourceEditAfterScan(false)
        try {
            const sku = product?.skuKut
            const fullModelNumber = product?.modelNumberKut
            const localizedProductName = product?.productName || product?.name
            const standardProductName = productData?.name

            // Build aliases so OCR can match full model, normalized model, and leading model prefix.
            const normalizedModel = fullModelNumber
                ? fullModelNumber.replace(/[^A-Za-z0-9]/g, '')
                : null
            const leadingModelPrefix = normalizedModel
                ? normalizedModel.match(/^[A-Za-z]+\d+/)?.[0]
                : null
            const modelAliases = [fullModelNumber, normalizedModel, leadingModelPrefix].filter(
                (value, index, array) =>
                    typeof value === 'string' &&
                    value.trim().length > 0 &&
                    array.indexOf(value) === index,
            )

            // Send both localized and catalog standard names when available
            const productNames = [...new Set([localizedProductName, standardProductName])].filter(
                (value) => typeof value === 'string' && value.trim().length > 0,
            )

            const products = []
            if (productNames.length > 0) {
                products.push({names: productNames, quantity: {min: 1, max: 3}})
            }
            if (modelAliases.length > 0) {
                products.push({
                    modelNumber: {allowed: modelAliases},
                    quantity: {min: 1, max: 3},
                })
            }
            if (sku) {
                const ean = productData?.ean
                const allowed = [...new Set([sku, ean].filter(Boolean))]
                products.push({sku: {allowed}, quantity: {min: 1, max: 3}})
            }

            const validation = {
                date: {
                    start: new Date(Date.now() - 10 * 365 * 24 * 60 * 60 * 1000).toISOString(),
                    end: new Date().toISOString(),
                },
                merchantName: {
                    allowed: merchantNameAllowList || [],
                    returnFromTheList: true,
                    required: false,
                },
                fraudDetection: {
                    similarityCheck: {
                        enabled: true,
                        required: true,
                    },
                },
                ...(products.length > 0 && {productLineItems: {products}}),
            }

            const fileBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(file)
            })

            const response = await fetch(`${appOrigin}${MRT_API_BASEPATH}/receipt-validation`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    fileBase64,
                    fileName: file.name,
                    fileMimeType: file.type,
                    validation,
                    referenceId: generateGUID(),
                    containerId: 'delonghi-loyalty-global',
                    language: lang || 'en',
                    incognito: false,
                }),
            })

            if (!response.ok) {
                console.error('Receipt validation error:', response.status)
                restoreRetrievedPurchaseData()
                setReceiptTaggunError('technical_error')
                return
            }

            const result = await response.json()
            setReceiptScanResult(result)

            if (result?.successful === false) {
                restoreRetrievedPurchaseData()
                setReceiptTaggunError('failed')
                return
            }

            if (typeof result?.successful !== 'boolean') {
                console.error('Taggun response missing successful field')
                restoreRetrievedPurchaseData()
                setReceiptTaggunError('technical_error')
                return
            }

            clearFieldsForReceiptData()

            const isMissingRetailer =
                result?.successful === true &&
                (result?.merchantName?.data === null ||
                    result?.merchantName?.data === undefined ||
                    result?.merchantName?.valid === false)

            if (isMissingRetailer) {
                const sourceOptions = Array.isArray(merchantNameAllowList)
                    ? merchantNameAllowList
                    : []
                const otherSource = sourceOptions.find((option) => {
                    if (typeof option === 'string') {
                        return option.trim().toLowerCase() === 'other'
                    }

                    const optionValue = option?.value
                    const optionLabel = option?.label

                    return (
                        (typeof optionValue === 'string' &&
                            optionValue.trim().toLowerCase() === 'other') ||
                        (typeof optionLabel === 'string' &&
                            optionLabel.trim().toLowerCase() === 'other')
                    )
                })

                const otherSourceValue =
                    typeof otherSource === 'string'
                        ? otherSource
                        : otherSource?.value || otherSource?.label

                if (otherSourceValue) {
                    form.setValue('source', otherSourceValue, {
                        shouldDirty: true,
                        shouldValidate: true,
                    })
                }

                setReceiptTaggunError('missing_retailer')
            }

            if (!isMissingRetailer && result?.merchantName?.data) {
                const normalizedMerchant = result.merchantName.data.trim().toLowerCase()
                const sourceOptions = Array.isArray(merchantNameAllowList)
                    ? merchantNameAllowList
                    : []
                const matchedSource = sourceOptions.find((option) => {
                    const optionValues =
                        typeof option === 'string' ? [option] : [option?.value, option?.label]

                    return optionValues.some((value) => {
                        if (typeof value !== 'string') return false

                        const normalizedValue = value.trim().toLowerCase()
                        return normalizedMerchant === 'ecommerce'
                            ? normalizedValue.startsWith('www')
                            : normalizedValue === normalizedMerchant
                    })
                })

                if (matchedSource) {
                    const sourceValue =
                        typeof matchedSource === 'string'
                            ? matchedSource
                            : matchedSource.value || matchedSource.label

                    if (sourceValue) {
                        form.setValue('source', sourceValue, {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                }
            }

            if (
                result?.fraudDetection &&
                Object.values(result.fraudDetection).some((check) => check?.valid === false)
            ) {
                restoreRetrievedPurchaseData()
                setReceiptTaggunError('failed')
                return
            }

            // Auto-fill purchase date if extracted with sufficient confidence
            if (result?.date?.data && result?.date?.confidenceLevel >= 0.7) {
                const extractedDate = new Date(result.date.data)
                if (!isNaN(extractedDate.getTime())) {
                    form.setValue('purchasedate', extractedDate)
                }
            }

            // Auto-fill price paid from Taggun totalAmount
            const extractedPrice = result?.matchedProductLineItems?.[0]?.unitPrice
            const purchasePrice = Number(String(extractedPrice ?? '').replace(',', '.'))
            form.setValue('pricePaid', extractedPrice?.toString() ?? '')
            setIsPricePaidLocked(Number.isFinite(purchasePrice) && purchasePrice !== 0)

            setReceiptValidated(true)
        } catch (err) {
            console.error('Receipt scan error:', err)
            restoreRetrievedPurchaseData()
            setReceiptTaggunError('technical_error')
        } finally {
            setIsReceiptScanning(false)
        }
    }

    const onSubmit = (data) => {
        // Chiama la funzione confirmEdit passando i dati aggiornati
        confirmEdit({
            ...product,
            productName: productData?.name,
            serialNumber: data.serialNumber,
            purchaseData: data.purchasedate,
            source: data?.source?.value || data?.source,
            pricePaid: data?.pricePaid ?? form.getValues('pricePaid'),
            receiptBase64,
            receiptName,
            receiptMime,
            hasNewReceipt: Boolean(receiptFile),
            receiptScanResult,
            receiptTaggunError,
            receiptValidated,
            isManualEntry: showManualFields,
            currency: activeCurrency,
            materialGroup: productData?.c_materialGroup,
        })
    }

    if (isGuest && !product) {
        return
    }

    return (
        <Flex {...styles.container}>
            {!isGuest && (
                <Flex {...styles.headerStepperContainer}>
                    <Button variant="text-button" onClick={goBack} {...styles.headerBackButton}>
                        <FormattedMessage
                            id="product_registration_form.back_to_registered_products"
                            defaultMessage="Back to registered products"
                        />
                    </Button>
                    <Text {...styles.headerTitle} as="h2">
                        <FormattedMessage
                            id="product_registration_form.complete_edit_registered_product"
                            defaultMessage="Update details"
                        />
                    </Text>
                </Flex>
            )}

            {/* <Flex {...styles.labelSearchInnerContainer}>
                <Text {...styles.labelSearchTitle}>
                    <FormattedMessage
                        id="product_registration_form.add_purchase_details"
                        defaultMessage="Add purchase details"
                    />
                </Text>
            </Flex> */}
            <Flex {...formStyles.outerContainer}>
                <Flex {...formStyles.innerContainer}>
                    <Image
                        display={['none', 'none', 'none', 'block']}
                        {...formStyles.image}
                        src={largeImage}
                        alt={product?.productName}
                    />
                    <Flex {...formStyles.formContainer}>
                        <Image
                            display={['block', 'block', 'block', 'none']}
                            {...formStyles.image}
                            src={smallImage}
                            alt={product?.productName}
                        />
                        <Text {...formStyles.titleText}>{productData?.name}</Text>
                        <Text {...formStyles.detailText}>
                            <FormattedMessage
                                id="product_registration_form.registration_details"
                                defaultMessage="Registration details"
                            />
                        </Text>
                        <Text {...formStyles.purchaseText}>
                            <FormattedMessage
                                id="product_registration_form.purchase_date_info"
                                defaultMessage="The purchase date cannot be changed, please delete the product registration and create a new one"
                            />
                        </Text>
                        <ProductRegistrationFields
                            form={form}
                            setReceiptBase64={setReceiptBase64}
                            setReceiptName={setReceiptName}
                            setReceiptMime={setReceiptMime}
                            initialFile={product.registeredProductAttachmentFolder}
                            isEditForm={true}
                            isPricePaidLocked={isPricePaidLocked}
                            isGuest={isGuest}
                            isAccount={true}
                            setReceiptFile={setReceiptFile}
                            fileError={fileError}
                            setFileError={setFileError}
                            allowSerialEdit={true}
                            onReceiptFileReady={handleReceiptScan}
                            isReceiptScanning={isReceiptScanning}
                            receiptValidated={receiptValidated}
                            receiptTaggunError={receiptTaggunError}
                            receiptScanResult={receiptScanResult}
                            allowSourceEdit={allowSourceEditAfterScan}
                            showManualFields={showManualFields}
                            onShowManualFields={() => setShowManualFields(true)}
                            onRetry={() => {
                                setReceiptTaggunError('')
                                setReceiptScanResult(null)
                                setReceiptValidated(false)
                                setShowManualFields(false)
                                setAllowSourceEditAfterScan(false)
                            }}
                        />
                    </Flex>
                </Flex>
                <Divider {...formStyles.horizontalDivider} />
            </Flex>
            <Flex {...formStyles.buttonContainer}>
                {isGuest ? (
                    <Button
                        {...formStyles.formButton}
                        variant={'primary'}
                        onClick={() => confirmDelete(product)}
                    >
                        <Text {...formStyles.buttonText}>
                            <FormattedMessage
                                id="product_registration_form.delete_registered_product"
                                defaultMessage="Delete"
                            />
                        </Text>
                    </Button>
                ) : (
                    <>
                        <Button
                            {...formStyles.formButtonSecondary}
                            variant={'secondary'}
                            onClick={goBack}
                        >
                            <Text {...formStyles.buttonText}>
                                <FormattedMessage
                                    id="product_registration_form.abort_edit_registered_product"
                                    defaultMessage="Cancel"
                                />
                            </Text>
                        </Button>
                        {(!receiptFile || receiptValidated || showManualFields) && (
                            <Button
                                {...formStyles.formButton}
                                variant={'primary'}
                                onClick={form.handleSubmit(onSubmit)}
                                data-testid="update-details-button"
                            >
                                <Text {...formStyles.buttonText}>
                                    <FormattedMessage
                                        id="product_registration_form.complete_edit_registered_product"
                                        defaultMessage="Update details"
                                    />
                                </Text>
                            </Button>
                        )}
                    </>
                )}
            </Flex>
        </Flex>
    )
}

ProductRegisteredEditForm.propTypes = {
    product: PropTypes.object,
    variant: PropTypes.string,
    selectedProduct: PropTypes.object,
    goBack: PropTypes.func,
    confirmEdit: PropTypes.func.isRequired,
    isGuest: PropTypes.bool,
    confirmDelete: PropTypes.func,
}

export default ProductRegisteredEditForm
